import { ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';

import {
  buildWorkerMarketingConsentAcceptance,
  generateSignupIdempotencyKey,
  useRecordMarketingConsentMutation,
} from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { Checkbox } from '@restworld/ui-components';
import { Button, Text } from '@restworld/ui-ds-base';
import { useAnalytics } from '@restworld/utils-common';

import { SignupMarketingConsentText } from '../legacy/terms-and-conditions';

export type MarketingConsentPlacement = 'interview_end' | 'applications';

/**
 * Whether this worker already answered during this visit.
 *
 * `/accounts/me` lives in the auth context, which cannot patch the user it holds, so `pending` stays
 * true until the next load and something else has to stop the card coming back. Session storage and
 * not a Jotai atom: the interview page mounts its own `Provider`, so an atom set there is not seen on
 * «Le mie candidature», and the key carries the user id so that a second worker logging in on the
 * same browser still gets the box. Unavailable storage only means the card can reappear, and the
 * backend then answers `already_chosen`.
 */
const answeredKey = (userId?: string) => `marketing_consent_answered:${userId}`;

const hasAnswered = (userId?: string) => {
  try {
    return sessionStorage.getItem(answeredKey(userId)) === '1';
  } catch {
    return false;
  }
};

const rememberAnswer = (userId?: string) => {
  try {
    sessionStorage.setItem(answeredKey(userId), '1');
  } catch {
    // See above: nothing depends on this write.
  }
};

/**
 * The optional signup box «Voglio ricevere novità e consigli da Restworld», offered after the
 * fact to the workers whose account was created without a screen (Hirematic/Indeed), who never
 * saw it. Same `Checkbox` and same label as signup, unticked; «Salva» records yes, «No, grazie»
 * records no. Renders nothing unless `/accounts/me` says the choice is still pending.
 */
export const MarketingConsentCard = ({
  placement,
  className,
}: {
  placement: MarketingConsentPlacement;
  /** Applied to the outer wrapper (margins, gutters), never to the card surface. */
  className?: string;
}) => {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const { trackEvent } = useAnalytics();
  const [answered, setAnswered] = useState(false);
  const [checked, setChecked] = useState(false);
  const [failed, setFailed] = useState(false);
  // One key per choice: a retry of the same answer sends the same key, a different answer a new one.
  const attempt = useRef<{ granted: boolean; key: string } | null>(null);
  const mutation = useRecordMarketingConsentMutation();
  // Set only after a «Salva» on this card: the other placement just disappears.
  const [confirmedHere, setConfirmedHere] = useState(false);

  if (confirmedHere) {
    return (
      <div className={className}>
        <div
          role="status"
          data-testid="marketing-consent-done"
          className="rounded-xl border border-green-200 bg-green-50 p-4 text-left"
        >
          <Text as="p" size={2} className="text-green-800">
            {t('marketing_consent.done')}
          </Text>
        </div>
      </div>
    );
  }

  if (answered || user?.marketing_consent_pending !== true || hasAnswered(user?.id)) return null;

  const submit = (granted: boolean) => {
    if (attempt.current?.granted !== granted) {
      attempt.current = { granted, key: generateSignupIdempotencyKey() };
    }
    setFailed(false);
    mutation.mutate(buildWorkerMarketingConsentAcceptance(granted, attempt.current.key), {
      onSuccess: (response) => {
        trackEvent('worker_marketing_consent_answered', { granted, placement });
        // `already_chosen`: the worker answered somewhere else first, so nothing to confirm here.
        if (granted && response?.data?.result !== 'already_chosen') setConfirmedHere(true);
        rememberAnswer(user?.id);
        setAnswered(true);
      },
      onError: () => setFailed(true),
    });
  };

  const busy = mutation.isPending;

  return (
    <div className={className}>
      <div
        data-testid="marketing-consent-card"
        className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left"
      >
        <Checkbox
          id={`marketing-consent-${placement}`}
          variant="box"
          title={
            <p className="text-gray-500 font-light leading-6 text-sm w-full inline-block">
              <SignupMarketingConsentText />
            </p>
          }
          isChecked={checked}
          mode="clickable"
          disabled={busy}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setChecked(event.target.checked)}
          testId="marketing-consent-checkbox"
        />
        {/* Indented to the label: the box is 16px wide plus the 10px gap of the Checkbox row. */}
        <div className="flex items-center gap-2 pl-[1.625rem]">
          <Button size="sm" variant="primary" disabled={!checked || busy} onClick={() => submit(true)}>
            {t('marketing_consent.save')}
          </Button>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => submit(false)}>
            {t('marketing_consent.no_thanks')}
          </Button>
        </div>
        {/* A plain paragraph: the DS `Text` takes no `role`, and the line must be announced. */}
        {failed ? (
          <p role="alert" className="pl-[1.625rem] text-sm text-red-700">
            {t('marketing_consent.error')}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default MarketingConsentCard;
